<script setup lang="ts">
/**
 * **「属性」Area 的主体** —— 从 `Playground.vue` 向下拆出来的子组件。
 *
 * ## 为什么按这里切
 *
 * 它是模板里**唯一一块还内联着的 Area 内容**（65 行），
 * 而它的样式（`.pg__props*`，33 条）也混在 `playground.css` 里。
 * 量下来依赖只有 4 个：文档元信息（3 个 props）+ 选中列表 + 当前活动节点。
 *
 * 抽出来之后：**模板、样式、以及将来的行编辑**都在同一个文件里 ——
 * 这正是这块该有的归属（`playground.css` 的注释里也写了
 * 「把行编辑搬过来是下一步」，那一步会落在这里）。
 *
 * ## 一条产品规矩（别改）
 *
 * ⚠️ **没传文档元信息时整段不显示** —— 内核不编造文档的身份。
 * 所以 `document*` 三个 props 都是可选的，三个都没有就整段不渲染。
 *
 * ## 归属
 *
 * **向下**：`Playground.vue` → `PlaygroundProperties.vue`，只有前者引用它。
 */
import { computed } from 'vue'
import type { GraphNodeModel } from '@/types/node'
import NpJsNodeSettings from '@/components/NpJsNodeSettings.vue'

const props = defineProps<{
  /** 文档名（接入方传；不传就不显示「文档」那段） */
  documentName?: string
  /** 文档 ID —— 只读，创建后不可改 */
  documentId?: string
  /** 文档尺寸（例如 `A4 · 210×297mm`） */
  documentSize?: string
  /** 当前选中的节点 id */
  selectedIds: string[]
  /** 当前活动节点 id（列表里高亮它） */
  activeNodeId: string | null
  /** 按 id 取模型（列表要显示每项的标题 / 类型） */
  modelOf: (id: string) => GraphNodeModel | undefined
  /**
   * **写一个参数值**（特殊设置用）。
   *
   * ⚠️ 走宿主（历史 + 校验 + 通知），不要直接改 model ——
   * 那样撤销记不住、别的窗口也不知道。
   *
   * `options.rebuild` = 这次改动要不要顺手重建节点的行（端口）。
   * 打字过程中**不要**重建：半截代码会把端口推歪（见 `NpJsNodeSettings`）。
   */
  setValue?: (
    nodeId: string,
    rowId: string,
    value: unknown,
    options?: { rebuild?: boolean },
  ) => void
  /**
   * 节点参数被改过的版本号（宿主维护）。
   *
   * ⚠️ 读它一下是**必须**的：控件值是普通对象的字段、不是响应式的，
   * 面板不读它就永远显示第一次读到的内容（改端口表点了没反应）。
   */
  editVersion?: number
}>()

/**
 * 当前活动节点（特殊设置针对它）。
 *
 * ⚠️ 只在**单选**时给特殊设置：多选时"改哪一个"没有明确答案，
 * 与其猜，不如不显示（下面给一句提示）。
 */
const activeModel = computed(() => {
  void props.editVersion
  /*
   * ⚠️ **跟着活动节点走，不是"恰好选中一个"**。
   *
   * 原来的条件是 `selectedIds.length === 1`，于是"活动节点明明切过去了、
   * 面板却空着" —— 因为选中态与活动节点是**两条**来源
   * （选中在图的 store 里，活动节点在宿主的一个 ref 里）。
   * 属性面板的语义本来就是"当前在编辑哪一个"，那就是活动节点 ✓。
   *
   * ⚠️ 多选时仍然给特殊设置：活动节点是**最后点的那一个**，
   * 改它的端口表不会歧义（歧义的是"改哪几个"，而这里只改一个）。
   */
  void props.selectedIds
  return props.activeNodeId ? props.modelOf(props.activeNodeId) : undefined
})

/** JS 节点的特殊设置（端口声明 + 代码） */
const JS_NODE = 'core.js'

function onSpecialValue(rowId: string, value: unknown, options?: { rebuild?: boolean }): void {
  const id = props.activeNodeId
  /*
   * ⚠️ 写不进去时**要出声**。原来的写法是 `if (!id || !props.setValue) return` ——
   * 静默 no-op：界面上表现为"点了没反应"，而排查的人完全不知道卡在哪一步
   * （这一版就在这里卡了半天）。宁可吵一点。
   */
  if (!id || !props.setValue) {
    // eslint-disable-next-line no-console
    console.warn(
      `[属性面板] 写不进去：${!id ? '没有活动节点' : '宿主没有传 setValue'}` +
        `（rowId=${rowId}）`,
    )
    return
  }
  props.setValue(id, rowId, value, options)
}
</script>

<template>
  <div class="pg__props">
        <!--
          ⚠️ 没传文档元信息时**整段不显示**（内核不编造文档身份）——
          内核不编造文档的身份。
        -->
        <section
          v-if="documentName || documentId || documentSize"
          class="pg__props-group"
        >
          <h4 class="pg__props-title">文档</h4>
          <div v-if="documentName" class="pg__props-row">
            <span class="pg__props-key">名称</span>
            <output class="pg__props-val">{{ documentName }}</output>
          </div>
          <div v-if="documentId" class="pg__props-row">
            <span class="pg__props-key">ID</span>
            <output class="pg__props-val">
              <code class="pg__legend-id">{{ documentId }}</code>
              <span class="pg__legend-lock" title="文档唯一 ID，创建后不可更改">🔒</span>
            </output>
          </div>
          <div v-if="documentSize" class="pg__props-row">
            <span class="pg__props-key">尺寸</span>
            <output class="pg__props-val">{{ documentSize }}</output>
          </div>
          <p class="pg__props-note">
            名称 / ID / 尺寸由接入方通过 props 传入（见 Playground 的
            <code>documentName</code> / <code>documentId</code> /
            <code>documentSize</code>），所以这里是只读的。
          </p>
        </section>

        <section class="pg__props-group">
          <h4 class="pg__props-title">
            选中
            <span v-if="selectedIds.length" class="pg__props-count">
              {{ selectedIds.length }}
            </span>
          </h4>

          <p v-if="!selectedIds.length" class="pg__props-empty">
            没有选中节点。在「节点图」里点一个节点，这里会列出它的属性。
          </p>

          <ul v-else class="pg__props-list">
            <li
              v-for="id in selectedIds"
              :key="id"
              class="pg__props-item"
              :class="{ 'is-active': activeNodeId === id }"
            >
              <span class="pg__props-item-name">
                {{ modelOf(id)?.label || modelOf(id)?.type || id }}
              </span>
              <span class="pg__props-item-type">{{ modelOf(id)?.type }}</span>
            </li>
          </ul>

          <!--
            ══════════════════════════════════════════════════════════
            **节点自己的特殊设置**

            ⚠️ 这里**不做节点参数的第二个入口**（用户定的话）：
            "节点本身就有控件支持调整数值，属性面板可以留着给节点用于
            节点的一些特殊的设置"。

            所以：普通节点在这里看到的是"没有特殊设置"；
            有特殊设置的类型（如今是 JS 节点）才长出内容。
            参数值永远在**节点上**就地改 —— 两个入口互相打架是这类界面
            最常见的坏味道（改了一处、另一处显示旧值）。
            ══════════════════════════════════════════════════════════
          -->
          <NpJsNodeSettings
            v-if="activeModel?.type === JS_NODE"
            :model="activeModel"
            :set-value="onSpecialValue"
            :edit-version="props.editVersion"
          />
          <p v-else-if="activeModel" class="pg__props-note" data-np-no-special>
            这个节点没有特殊设置 —— 它的参数在节点上直接改。
          </p>
          <p v-else-if="selectedIds.length > 1" class="pg__props-note">
            选中了多个节点，特殊设置一次只对一个显示。
          </p>
        </section>
  </div>
</template>


<!--
  ⚠️ 样式用**非 scoped**：这份要盖住的元素全在本组件自己的模板里，
  但 `code` / `output` 这类是原生标签（scoped 也能命中）；
  用非 scoped 是为了和它们在 `playground.css` 里时的**层叠位置一致** ——
  那 33 条规则原本排在文件末尾，改成 scoped 会多一层属性选择器，
  可能压过别处同特异度的规则。保守起见保持原文。
-->
<style>
.pg__props {
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1 1 auto;
  min-height: 0;
  padding: 12px 14px;
  overflow-y: auto;
  overscroll-behavior: contain;
  background: var(--np-surface-0, #1b1b1b);
  color: var(--np-panel-text);
  font-size: 11px;
}

.pg__props-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.pg__props-title {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding-bottom: 5px;
  border-bottom: 1px solid var(--np-border-soft);
  color: var(--np-panel-dim);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
}

.pg__props-count {
  padding: 0 5px;
  border-radius: var(--np-radius-s);
  background: var(--np-panel-chip-bg);
  font-variant-numeric: tabular-nums;
}

.pg__props-row {
  display: grid;
  grid-template-columns: 52px 1fr;
  align-items: baseline;
  gap: 8px;
}

.pg__props-key {
  color: var(--np-panel-dim);
}

.pg__props-val {
  display: flex;
  align-items: baseline;
  gap: 4px;
  min-width: 0;
  color: var(--np-panel-text);
  word-break: break-all;
}

.pg__props-note {
  margin: 2px 0 0;
  color: var(--np-panel-mute);
  font-size: 10px;
  line-height: 1.5;
}

.pg__props-note code {
  padding: 0 3px;
  border-radius: 3px;
  background: var(--np-panel-chip-bg);
  font-size: 9.5px;
}

.pg__props-empty {
  margin: 0;
  color: var(--np-panel-mute);
  line-height: 1.6;
}

.pg__props-list {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.pg__props-item {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 7px;
  border-radius: var(--np-radius-s);
  background: var(--np-surface-1);
}

.pg__props-item.is-active {
  background: var(--np-accent-soft);
  color: var(--np-accent);
}

.pg__props-item-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pg__props-item-type {
  flex: 0 0 auto;
  color: var(--np-panel-mute);
  font-size: 10px;
}
</style>
